import { cn } from "@/lib/utils";

// Map full brand names (as they appear on card payloads) to icon slugs.
// Unknown slugs fall through to the monogram fallback.
const BRAND_SLUG_MAP = {
  "AgenticTrust": "at",
  "AeriusView": "av",
  "WingPoint": "wp",
  "TrustOffice": "trustoffice",
  "TrustMinutes": "tm",
  "SocializeVideo": "sv",
  "StenoDesk": "sd",
  "TrueJoyBirthing": "tjb",
  "True Joy Birthing": "tjb",
};

// Sidebar keys → slugs (sidebar already uses short keys, but this is idempotent).
const KEY_SLUG_MAP = {
  tjb: "tjb",
  at: "at",
  to: "trustoffice",
  av: "av",
  wp: "wp",
  tm: "tm",
  sv: "sv",
  sd: "sd",
};

const knownSlugs = new Set(["trustoffice", "at", "av", "wp", "tm", "sv", "sd", "tjb"]);

export const resolveSlug = (input) => {
  if (!input) return null;
  const direct = BRAND_SLUG_MAP[input] || KEY_SLUG_MAP[input];
  if (direct) return direct;
  const normalized = String(input).toLowerCase().replace(/\s+/g, "");
  const rev = Object.fromEntries(
    Object.entries(BRAND_SLUG_MAP).map(([k, v]) => [k.toLowerCase().replace(/\s+/g, ""), v])
  );
  return rev[normalized] || null;
};

export const BrandIcon = ({ brand, slug, className, size = 18 }) => {
  const resolved = slug ? slug : resolveSlug(brand);
  const hasIcon = resolved && knownSlugs.has(resolved);
  const monogram = (brand || resolved || "?").charAt(0).toUpperCase();

  if (!hasIcon) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-md bg-os-wash text-os-ink-3 select-none",
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.55) }}
        aria-hidden="true"
        title={brand || resolved || ""}
      >
        {monogram}
      </span>
    );
  }

  // 2026-10-26 (Jeff): av.svg + tjb.svg had been wired to the WRONG assets —
  // a gold AAV-project shield and the white TJB wordmark. Both now serve the
  // REAL brand assets copied byte-for-byte from each brand's protected
  // branding folder (True Joy Birthing - Icon.svg; booking-app production
  // copy of the AeriusView mark). The dark-seat workaround below is no
  // longer needed for tjb (the lavender mark reads on light backgrounds).
  // The asset files themselves are NEVER modified — brand-identity hard block.
  const WHITE_SEATS = new Set([]);
  const seat = WHITE_SEATS.has(resolved);

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        seat && "rounded-full bg-os-ink p-[2px]",
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <img
        src={`/brand-icons/${resolved}.svg`}
        alt=""
        className="inline-block h-full w-full object-contain"
        onError={(e) => {
        // If SVG fails to load, swap to monogram fallback visually
        e.currentTarget.style.display = "none";
        const parent = e.currentTarget.parentElement;
        if (parent) {
          const fallback = document.createElement("span");
          fallback.className = "inline-flex shrink-0 items-center justify-center rounded-md bg-os-wash text-os-ink-3 select-none";
          fallback.style.cssText = `width:${size}px;height:${size}px;font-size:${Math.round(size * 0.55)}px`;
          fallback.setAttribute("aria-hidden", "true");
          fallback.textContent = monogram;
          parent.appendChild(fallback);
        }
      }}
      />
    </span>
  );
};
